import { useUserProfile } from '@/context/UserProfileContext';
import { api } from '@/utils/api';
import { getImageFullUrl } from '@/utils/imageUtils';
import env from '@/utils/loadEnv';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useState, useCallback } from 'react';
import { postEventService } from '@/services/PostEventService';

// Define the post schema
const postSchema = z.object({
  caption: z.string().max(1024, 'Caption must be 1024 characters or less'),
  audience: z.number().min(0).max(2),
  courseId: z.string().optional(),
  profileId: z.number(),
  hashtags: z.array(z.string()).optional(),
  media: z.array(z.object({
    id: z.string(),
    type: z.enum(['image', 'video']),
    url: z.string(),
    uploading: z.boolean().optional()
  })).optional()
});

export type PostFormValues = z.infer<typeof postSchema>;

export type MediaItem = {
  id: string;
  type: 'image' | 'video';
  url: string;
  uploading?: boolean;
  file?: File;
  fileContent?: string;
  error?: boolean;
};

type UsePostFormProps = {
  addPostToFeed?: (post: any) => void;
  onOpenChange: (open: boolean) => void;
};

export function usePostForm({ addPostToFeed, onOpenChange }: UsePostFormProps) {
  const { profile: userProfile } = useUserProfile();
  const [loading, setLoading] = useState(false);
  const [published, setPublished] = useState(false);
  const [detectedHashtags, setDetectedHashtags] = useState<string[]>([]);

  const form = useForm<PostFormValues>({
    resolver: zodResolver(postSchema),
    defaultValues: {
      caption: '',
      audience: 0,
      courseId: '',
      profileId: userProfile?.id || 0,
      hashtags: [],
      media: []
    },
  });

  // Extract hashtags from caption text
  const extractHashtags = (text: string): string[] => {
    const hashtagRegex = /#([\w]+)/g;
    const matches = text.match(hashtagRegex);
    if (!matches) return [];

    // Remove the # symbol and return unique hashtags
    return [...new Set(matches.map(tag => tag.substring(1)))];
  };

  // Handle hashtag selection from the suggestion dropdown
  const handleHashtagSelect = (hashtag: string) => {
    if (!detectedHashtags.includes(hashtag)) {
      setDetectedHashtags(prev => [...prev, hashtag]);
    }
  };

  const resetForm = useCallback(() => {
    setDetectedHashtags([]);
    form.reset({
      caption: '',
      audience: 0,
      courseId: '',
      profileId: userProfile?.id || 0,
      hashtags: [],
      media: []
    });
  }, [form, userProfile?.id]);

  const onSubmit = async (data: PostFormValues, media: MediaItem[]) => {
    if (!userProfile) return;
    
    setLoading(true);
    const formData = new FormData();

    // Extract hashtags from the caption
    const extractedHashtags = extractHashtags(data.caption);
    const allHashtags = [...new Set([...detectedHashtags, ...extractedHashtags])];

    formData.append('caption', data.caption);
    formData.append('profileId', data.profileId.toString());
    formData.append('audience', data.audience.toString());
    if (data.courseId) {
      formData.append('courseId', data.courseId);
    }

    // Add hashtags to the form data
    allHashtags.forEach(hashtag => {
      formData.append('hashtags[]', hashtag);
    });

    // Prepare media data for the API
    media.forEach((item: { type: string; id: string }) => {
      if (item.type === 'image') {
        formData.append('images[]', item.id);
      } else if (item.type === 'video') {
        formData.append('videos[]', item.id); // Cloudflare Stream UID
      }
    });

    try {
      const response = await api.post(`${env.VITE_PUBLIC_API_URL}/posts`, formData);

      // Prepare the post data for the feed
      const postToAdd = {
        user: userProfile,
        ...response.data.post,
        media: media.map(item => ({
          url: item.type === 'image' ? getImageFullUrl(item.id) : item.url,
          type: item.type
        }))
      };

      // Call addPostToFeed if provided directly
      if (addPostToFeed) {
        addPostToFeed(postToAdd);
      }
      
      // Also emit an event so other components can react to the new post
      postEventService.emit('postCreated', postToAdd);
      
      setPublished(true);
      setTimeout(() => {
        onOpenChange(false);
        setPublished(false);
      }, 1000);
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    loading,
    published,
    detectedHashtags,
    setDetectedHashtags,
    extractHashtags,
    handleHashtagSelect,
    resetForm,
    onSubmit,
  };
}
